import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart-kr.svg';
import SpecialCore from '../../components/specialCoreAnalysis/specialCore';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { Button, HelpWindow, PageHeader, Tabs, TabList, Tab, krHelpTopics } from '../../design-system';
import { FiHelpCircle } from 'react-icons/fi';
import { listAllProjectSamples } from '../../api/data';
import { Sample } from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import ProjectShell from '../../components/layout/ProjectShell';
import LoadSampleModal from '../CapillaryPressurePage/LoadSampleModal';
import { useSampleHistory } from '../CapillaryPressurePage/useSampleHistory';
import KrAnalysisSummary from './KrAnalysisSummary';
import styles from './KrAnalysisPage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

const KrAnalysisPage = () => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { updateData, data } = useAppContext();
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');

  const [activeTab, setActiveTab] = useState('sample-information');
  const [modalOpen, setModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [sampleCount, setSampleCount] = useState<number | null>(null);
  const {
    history: sampleHistory,
    addToHistory,
    removeFromHistory
  } = useSampleHistory(projectUid);

  useEffect(() => {
    if (!projectUid) return;
    listAllProjectSamples(projectUid)
      .then(data => setSampleCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setSampleCount(0));
  }, [projectUid]);

  const handleSelectSample = (sample: Sample, allSamples: Sample[], well?: Well) => {
    setSampleCount(allSamples.length);
    const wellSamples = allSamples.filter(s => s.well_uid === sample.well_uid);
    updateData({ wellSamples });
    const resolvedWell =
      well ||
      data.wells.find(entry => entry.uid === sample.well_uid) ||
      (selectedInfo.well?.uid === sample.well_uid ? selectedInfo.well : undefined);
    setSelectedInfo({ sample, well: resolvedWell });
    addToHistory(sample);
    setModalOpen(false);
  };

  const handleSelectMany = (picked: Sample[], allSamples: Sample[]) => {
    setSampleCount(allSamples.length);
    if (picked.length === 0) return;
    const last = picked[picked.length - 1];
    const wellSamples = allSamples.filter(s => s.well_uid === last.well_uid);
    updateData({ wellSamples });
    const resolvedWell =
      data.wells.find(entry => entry.uid === last.well_uid) ||
      (selectedInfo.well?.uid === last.well_uid ? selectedInfo.well : undefined);
    picked.forEach(s => addToHistory(s));
    setSelectedInfo({ sample: last, well: resolvedWell });
    setModalOpen(false);
  };

  const handleCloseTab = (closed: Sample) => {
    const list = sampleHistory;
    const idx = list.findIndex(s => s.uid === closed.uid);
    removeFromHistory(closed.uid);
    if (selectedInfo.sample?.uid !== closed.uid) return;
    const next = list[idx + 1] ?? list[idx - 1] ?? null;
    if (next) {
      setSelectedInfo({ sample: next, well: selectedInfo.well });
    } else {
      setSelectedInfo({ sample: undefined, well: undefined });
    }
  };

  const handleSelectRecent = async (sample: Sample) => {
    if (!projectUid) return;
    try {
      const allSamples = await listAllProjectSamples(projectUid);
      const normalizedSamples = Array.isArray(allSamples) ? allSamples : [];
      const fresh = normalizedSamples.find(s => s.uid === sample.uid);
      if (!fresh) {
        removeFromHistory(sample.uid);
        return;
      }
      handleSelectSample(fresh, normalizedSamples);
    } catch {
      removeFromHistory(sample.uid);
    }
  };

  return (
    <ProjectShell>
      <div className={styles.page}>
        <div className={styles.stickyBar}>
          <PageHeader
            title="Kr Analysis"
            sample={selectedInfo.sample ?? null}
            sampleCount={sampleCount}
            recentSamples={sampleHistory}
            onOpen={() => setModalOpen(true)}
            onSelectRecent={handleSelectRecent}
            onCloseTab={handleCloseTab}
            actions={
              <>
                {selectedInfo.sample && (
                  <Button variant="secondary" onClick={() => setSummaryOpen(true)}>
                    View Sample Summary
                  </Button>
                )}
                <Button variant="secondary" startIcon={<HelpIcon size={14} />} onClick={() => setHelpOpen(true)}>
                  Help
                </Button>
              </>
            }
          />
          <nav className={styles.pageNav}>
            <Tabs value={activeTab} onChange={setActiveTab} variant="underline" size="md">
              <TabList>
                <Tab value="sample-information">Sample Information</Tab>
                <Tab value="sample-measurements">Sample Measurements</Tab>
                <Tab value="refinement-variables">Refinement Variables</Tab>
                <Tab value="corey-exponents">Corey Exponents</Tab>
                <Tab value="refinement-curves">Refinement Curves</Tab>
              </TabList>
            </Tabs>
          </nav>
        </div>

        {!selectedInfo.sample ? (
          <div className={styles.emptyState}>
            <EmptyChartIllustration style={{width: "420px"}}/>
            <p className={styles.emptyTitle}>No samples loaded</p>
            <p className={styles.emptySubtitle}>
              Select a project and well to load Kr analysis data.
            </p>
            <Button variant="primary" onClick={() => setModalOpen(true)}>
              Load Sample
            </Button>
          </div>
        ) : (
          <div className={styles.content}>
            <SpecialCore activeTab={activeTab} />
          </div>
        )}
      </div>

      <LoadSampleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleSelectSample}
        onSelectMany={handleSelectMany}
        projectUid={projectUid}
      />

      <HelpWindow
        topics={krHelpTopics}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      <KrAnalysisSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} />
    </ProjectShell>
  );
};

export default KrAnalysisPage;
